import {pageAuthorizedByGroup} from "../fragments/PageAuthorization";
import {Link, useParams} from "react-router-dom";
import {useQuery} from "@apollo/client";
import {graphQLServices} from "../../services/GraphQLServices";
import React, {useState} from "react";
import {
	Autocomplete,
	Box,
	Breadcrumbs,
	CircularProgress,
	IconButton,
	Paper,
	Stack,
	TextField,
	Typography
} from "@mui/material";
import {cloneMutationData} from "../products/Utils";
import {PentaSplit} from "../../components/common/PentaSplit";
import {ProcessButton} from "../../components/common/input/ProcessButton";
import {
	ANALYZE_CHANGES,
	EXISTS_TRANSLATION_ITEM,
	GET_TRANSLATION_ITEM,
	MODULES_INDEX,
	PHRASES_INDEX,
	PUT_TRANSLATION_ITEM
} from "./Queries";
import {PhraseItem} from "./models/PhraseItem";
import {translationsEditorTargetStage} from "./TranslationsOverview";
import {TabPanel} from "../../components/common/TabPanel";
import {TranslationItem} from "./models/TranslationItem";
import {Module} from "./models/Module";
import {Languages} from "./Data";
import {translateFunction} from "../../localization/TranslationService";
import {DifferenceSet} from "./models/DifferenceSet";
import {deepClone, normalizeData} from "../../libraries/util/Functions";
import {TranslationDifferences} from "./TranslationsChangesOverview";

const translate = translateFunction("SureBasePortalTranslations");

export const TranslationItemDetails = pageAuthorizedByGroup(function () {
	const id = useParams()?.id;
	const [modules, setModules] = useState<Module[]>();
	const [phrases, setPhrases] = useState<PhraseItem[]>();
	const [translationItem, setTranslationItem] = useState<TranslationItem>();
	const [lastUpdated, setLastUpdated] = useState(0);
	
	const modulesIndex = useQuery(MODULES_INDEX, {
		client: graphQLServices.translationsClient,
		fetchPolicy: "no-cache",
		variables: {
			stage: translationsEditorTargetStage,
		}
	});
	
	const phrasesIndex = useQuery(PHRASES_INDEX, {
		client: graphQLServices.translationsClient,
		fetchPolicy: "no-cache",
		variables: {
			stage: translationsEditorTargetStage,
		}
	});
	
	if (!modulesIndex.loading && !modules) {
		const nodes: Module[] = modulesIndex.data.modules.nodes;
		setModules(nodes);
	}
	
	if (!phrasesIndex.loading && !phrases) {
		const nodes: PhraseItem[] = phrasesIndex.data.phraseItems.nodes;
		setPhrases(nodes);
	}
	
	const {loading, data, error} = useQuery(GET_TRANSLATION_ITEM, {
		client: graphQLServices.translationsClient,
		fetchPolicy: "no-cache",
		variables: {
			stage: translationsEditorTargetStage,
			id: id,
		}
	});
	
	if (loading || phrasesIndex.loading || modulesIndex.loading)
		return <Box sx={{display: "flex", width: "100%", height: "100%"}}>
			<CircularProgress style={{margin: "auto"}}/>
		</Box>;
	
	if (error)
		throw error;
	
	const nodes = data.translationItems.nodes;
	if (nodes === null || nodes.length === 0)
		throw new Error(`TranslationItem with id ${id} was not found.`);
	
	const node: TranslationItem = nodes[0];
	if (!translationItem && !phrasesIndex.loading && !modulesIndex.loading) {
		setTranslationItem(node);
		return <Box sx={{display: "flex", width: "100%", height: "100%"}}>
			<CircularProgress style={{margin: "auto"}}/>
		</Box>;
	}
	
	
	const props = {
		translationItem: translationItem,
		setTranslationItem: setTranslationItem,
		modules: modules ?? [],
		phrases: phrases ?? []
	};
	
	const handleSave = async (existingTranslationItem:TranslationItem) => {
		await translationItemExists(existingTranslationItem).then(async (x) => {
			if (x) {
				throw new Error(`Item already exists`);
			}
			
			await saveChanges(existingTranslationItem);
		});
		
		setLastUpdated(performance.now());
	};
	return <PentaSplit
		top={<Breadcrumbs style={{margin: "5px"}}>
			<Link style={{color: "inherit", textDecoration: "unset"}} to="/translations_setup">{translate("Translations management")}</Link>
			<Link style={{color: "inherit", textDecoration: "unset"}} to="/translations_setup/translations">{translate("Translations")}</Link>
			<Typography color="text.primary">{id}</Typography>
		</Breadcrumbs>}
		center={<Paper style={{
			height: "100%",
		}}>
			<Stack style={{height: "100%"}}>
				<div style={{flexGrow: 1, overflow: "hidden"}}>
					<Information
						key={lastUpdated}
						{...props as TranslationItemProps}
					/>
				</div>
				<div style={{flexGrow: 0, padding: "10px"}}>
					<ProcessButton
						defaultText={translate("Save")}
						inProgressText={translate("Saving")}
						completedText={translate("Saved")}
						resetKeys={[translationItem]}
						variant={"outlined"}
						onClick={() => handleSave(translationItem as TranslationItem)}/>
				</div>
			</Stack>
		</Paper>}
	/>;
}, ["Admin", "Translations"]);

async function translationItemExists(translationItem: TranslationItem): Promise<boolean> {
	
	const existsItems = await graphQLServices.translationsClient.query(
		{
			query: EXISTS_TRANSLATION_ITEM,
			variables: {
				stage: translationsEditorTargetStage,
				phraseId: translationItem.phraseId,
				moduleId: translationItem.moduleId,
				languageCode: translationItem.languageCode
			},
			fetchPolicy: "no-cache"
		}
	);

	const nodes: any[] = existsItems.data.translationItems.nodes;
	return nodes.filter(x => x.id !== translationItem.id).length > 0;
}

async function saveChanges(translationItem: TranslationItem) {
	await graphQLServices.translationsClient.mutate({
		mutation: PUT_TRANSLATION_ITEM,
		variables: {
			stage: translationsEditorTargetStage,
			translation: cloneMutationData(translationItem),
		}
	});
}

interface TranslationItemProps {
	readonly modules: Module[];
	readonly phrases: PhraseItem[];
	readonly translationItem: TranslationItem;
	readonly setTranslationItem: (translationItem: TranslationItem) => void;
}

function Information(props: TranslationItemProps) {
	return <TabPanel value={0} index={0}>
		<div style={{height: "100%", overflow: "auto"}}>
			<Stack spacing={2}>
				<div>{translate("Translation Information")}</div>
				<Autocomplete
					disablePortal
					options={Languages}
					autoSelect
					autoComplete
					renderInput={(params) => <TextField
						{...params}
						label={translate("Language")}
						variant={"standard"}
					/>}
					value={Languages.find(x => x.languageCode === props.translationItem.languageCode)}
					getOptionLabel={(it: any) => translate(it.name)}
					onChange={(event, item) => {
						props.setTranslationItem({...props.translationItem, languageCode: item?.languageCode ?? ""});
					}}
				/>
				
				<Autocomplete
					disablePortal
					options={props.modules}
					autoSelect
					autoComplete
					renderInput={(params) => <TextField
						{...params}
						label={translate("Module")}
						variant={"standard"}
					/>}
					value={props.modules.find(x => x.id === props.translationItem.moduleId)}
					getOptionLabel={(it: any) => translate(it.name)}
					onChange={(event, item) => {
						props.setTranslationItem({...props.translationItem, moduleId: item?.id ?? ""});
					}}
				/>
				
				<Autocomplete
					disablePortal
					options={props.phrases}
					autoSelect
					autoComplete
					renderInput={(params) => <TextField
						{...params}
						label={translate("Phrase")}
						variant={"standard"}
					/>}
					value={props.phrases.find(x => x.id === props.translationItem.phraseId)}
					getOptionLabel={(it: any) => it.phrase}
					onChange={(event, item) => {
						props.setTranslationItem({...props.translationItem, phraseId: item?.id ?? ""});
					}}
				/>
				
				<TextField
					label={translate("Translation")}
					value={props.translationItem.translation}
					variant={"standard"}
					onChange={event => props.setTranslationItem({
						...props.translationItem,
						translation: event.target.value
					})}
				/>
				
				{props.translationItem.locationUrl && (
					<div>
						<a
							href={props.translationItem.locationUrl}
							target="_blank"
							rel="noopener noreferrer"
							style={{ display: "block", marginTop: "8px" }}
						>
							{props.translationItem.locationUrl}
						</a>
					</div>
				)}
				<div>
					<IconButton
						color={"primary"}
						onClick={() => {
							props.setTranslationItem({...props.translationItem});
						}}
					>
					</IconButton>
				</div>
				
				<Differences translationItem={props.translationItem}/>
			</Stack>
		</div>
	</TabPanel>;
}

interface DifferencesProps {
	readonly translationItem: TranslationItem;
}

function Differences(props: DifferencesProps) {
	const {translationItem} = props;
	return <TranslationDifferences
		variables={{
			idFilter: [
				translationItem.moduleId,
				translationItem.phraseId,
				translationItem.id,
			]
		}}
	/>;
}

export function openTranslationItemDetailsFor(id: string) {
	window.location.href = linkPhraseItemDetailsFor(id);
}

export function linkPhraseItemDetailsFor(id: string): string {
	return `/translations_setup/translations/${encodeURIComponent(id)}`;
}
